import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import { OUTPUT_TTL } from '../../constants/Pac';
import {
  MaterialType,
  BatchAction,
} from '../../constants/MaterialModels';
import convertUnit from '../../utils/unit-converter';
import {
  WeighedDataToBatch,
  MeteredDataToBatch,
} from '../../containers/Batch/DataToBatch';
import { BatchCycleStatus } from '../../constants/BatchCycleStatus';
import { ScaleTypes } from '../discharge-store';
import moment from 'moment';
import { IBatchData } from '../../containers/Batch/IBatchData';
import {
  getSecondWaterPump,
  getWaterPump,
} from './water-pump-checks';
import { IScale } from '../../../types/device/DeviceModels';
import { IMeteredDataToBatch } from '../../../types/batch/DataToBatch';
import {
  RemoteBatchData,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';

export const ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING = [
  DeviceStatus.MIXING,
  DeviceStatus.END_MIXING,
  DeviceStatus.TOP_OPEN,
  DeviceStatus.RETURNING,
];

export const batchActionMixin = {

  onBatchAction(this: IBatchStore, data: IBatchData, action = BatchAction.StartBatching) {

    logger.log('on batch action:', action);

    if (!data.pumps || !this.isActiveDrop(data)) {
      return;
    }

    if (data.materialId && data.mixerId && !this.mixerIsReadyForDischarge(data)) {
      this.timers.set(timerKey.batchAction(), () => this.onBatchAction(data, action), 2000, 'Batch action');
      return;
    }

    if (!data.batchActionFired.includes(action)) {
      data.batchActionFired.push(action);
    }

    Object.keys(data.pumps).forEach(pumpId => {
      const pumpData = data.pumps[pumpId];
      const { pump, material, materialDetails } = pumpData;
      let d = this.getDeviceStatus(pump.id);
      logger.log('on batch action', action, pump.id, material.code, d.status);

      if (pumpData.isDischarged) {
        logger.log('pump already discharged', pump.id, material.code, d.status);
        return;
      }

      if (d.status !== DeviceStatus.IDLE && d.status !== DeviceStatus.FINISH
        && data.batchActionFired.includes(materialDetails.batchStart)
        && d.uid && (d.uid !== data.uid || d.currentDrop !== data.currentDrop)) {
        this.timers.clear(timerKey.metered(pumpId));

        this.timers.set(timerKey.metered(pumpId), () => this.onBatchAction(data, action), 2000, `Metered action - pump #${pumpId}`);
        logger.log(`pump #${pumpId} not ready for batch, try again`, material.code, d.status, d.uid, data.uid);
        return;
      }

      if (d.status === DeviceStatus.FINISH && (d.uid !== data.uid || d.currentDrop !== data.currentDrop)) {
        d = this.getDeviceStatusDefault(pumpData.pump);
        logger.log(`pump #${pumpId} reset status`, material.code, d.status, d.uid, data.uid);
      }

      if (d.status !== DeviceStatus.IDLE && d.status !== DeviceStatus.FINISH && d.status !== DeviceStatus.WAIT_DISCH && d.status !== DeviceStatus.WAIT_WATER && d.status !== DeviceStatus.WAIT_MOIST) {
        logger.log(`pump #${pumpId} not ready for batch`, material.code, d.status, d.uid, data.uid);
        return;
      }

      if (d.status === DeviceStatus.FINISH && d.uid === data.uid && d.currentDrop === data.currentDrop) {
        const tankStatus = this.getDeviceStatus(pump.tankId);
        if (!pump.tankId || tankStatus.status !== DeviceStatus.WAIT_DISCH || tankStatus.uid !== data.uid || tankStatus.currentDrop !== data.currentDrop) {
          logger.log(`pump #${pumpId} already batched`, material.code, d.status, d.uid, data.uid);
          return;
        }
      }

      if (d.status === DeviceStatus.WAIT_DISCH && (d.uid !== data.uid || d.currentDrop !== data.currentDrop)) {
        logger.log(`pump #${pumpId} waiting for discharge`, material.code, d.status, d.uid, data.uid);
        return;
      }

      if (!this.isBottlePump(pump)) {

        if (this.isPumpWithTank(pump)
          && data.batchActionFired.includes(materialDetails.dischargeStart)
          && (d.status === DeviceStatus.FINISH || d.status === DeviceStatus.WAIT_WATER)) {
          const tankStatus = this.getDeviceStatus(pump.tankId);
          if (tankStatus.status === DeviceStatus.WAIT_DISCH && tankStatus.uid === data.uid && tankStatus.currentDrop === data.currentDrop) {
            logger.log(`ON BATCH ACTION: NON BOTTLE #${pump.id} DISCHARGE TANK #${pump.tankId}`, material.code, data.uid, data.currentDrop);
            this.dischargeTank(pump.tankId, pumpData, data, (data: IBatchData) => {
              const waterPump = getWaterPump(this.rootStore, data) || {};
              const secondWaterPump = getSecondWaterPump(this.rootStore, data) || {};

              if (waterPump.id === pump.id || secondWaterPump.id === pump.id) {
                if (data.headWater && data.headWaterPumpDischarged && !data.headWaterTankDischarged) {
                  data.headWaterTankDischarged = true;
                  this.resumePrimaryScaleDischarge(data);
                }

                if (!data.washWater || (data.washWater && data.washWaterPumpDischarged)) {
                  if (waterPump.id && data.pumps[waterPump.id]) {
                    data.pumps[waterPump.id].isDischarged = true;
                  }
                  if (secondWaterPump.id && data.pumps[secondWaterPump.id]) {
                    data.pumps[secondWaterPump.id].isDischarged = true;
                  }
                  data.washWaterTankDischarged = true;
                }

                if (data.washWater && !data.washWaterTankDischarged && data.headWater && data.headWaterTankDischarged) {
                  if (waterPump.id && data.pumps[waterPump.id]) {
                    this.batchMetered(data.pumps[waterPump.id], data, null, () => {
                      this.onFinishNonBottlePump(data.pumps[waterPump.id], data, action);
                    });
                  }
                  if (secondWaterPump.id && data.pumps[secondWaterPump.id]) {
                    this.batchMetered(data.pumps[secondWaterPump.id], data, null, () => {
                      this.onFinishNonBottlePump(data.pumps[secondWaterPump.id], data, action);
                    });
                  }

                }
              } else {
                data.pumps[pump.id].isDischarged = true;
              }

              if (data?.materialId) {
                if (data.mixerId) {
                  this.dischargeMixer(data);
                } else {
                  this.finishDischarge(data);
                }
              }
            });
          }
        } else if (d.status !== DeviceStatus.FINISH
          && (data.batchActionFired.includes(materialDetails.batchStart)
            || data.batchActionFired.includes(materialDetails.dischargeStart)
            || data.materialId)) {

          if (!this.timers.has(timerKey.device(pump.id))) {
            this.timers.set(timerKey.device(pump.id), () => {

              this.batchMetered(pumpData, data, null, () => {
                this.onFinishNonBottlePump(pumpData, data, action);
              });

            }, 1000, `Batch metered – Pump ${pump.id}`);
          }
        }

      } else if (d.status !== DeviceStatus.WAIT_DISCH
        && (data.batchActionFired.includes(materialDetails.batchStart) || data.materialId)) {
        this.checkPumpIsEmpty(pumpData, data, (pumpData, data) => {
          const { pump, material } = pumpData;
          logger.log(`ON BATCH ACTION: BATCH BOTTLE PUMP #${pump.id}`, material.code, data.uid, data.currentDrop);

          if (!this.timers.has(timerKey.device(pump.id))) {
            this.timers.set(timerKey.device(pump.id), () => {

              this.batchMetered(pumpData, data, null, () => {
                if (data.batchActionFired.includes(materialDetails.dischargeStart)) {
                  logger.log(`ON BATCH ACTION: DISCHARGE BOTTLE PUMP #${pump.id}`, material.code, data.uid, data.currentDrop);
                  this.dischargeBottlePump(pumpData, data, () => {
                    if (!this.isPumpWithTank(pump)) {
                      data.pumps[pump.id].isDischarged = true;
                    }
                  });
                }
              });

            }, 1000);
          }

        });
      } else if (data.batchActionFired.includes(materialDetails.dischargeStart)) {
        this.checkCanDischargePump(pumpData, data, (pumpData, data) => {
          logger.log(`ON BATCH ACTION: DISCHARGE BOTTLE PUMP #${pump.id}`, material.code, data.uid, data.currentDrop);
          this.dischargeBottlePump(pumpData, data, () => {
            if (!this.isPumpWithTank(pump)) {
              data.pumps[pump.id].isDischarged = true;
            }
          });
        });
      }

    });
  },

  getMeteredDataToBatch(this: IBatchStore, materialId: number, constituent: any, data: IBatchData) {

    let error = '';

    const material = constituent?.batchCode ? this.materialStore.getMaterialByBatchCode(constituent.batchCode) : this.materialStore.getMaterialByItemId(materialId);

    if (!material) {
      error = `No material: #${materialId} on the plant! `;
      logger.warn(error);
      return { error };
    }

    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    if (!materialDetails) {
      error = `No material details: ${material.code}! `;
      logger.warn(error, material);
      return { error };
    }

    const pump = this.devicesStore.getDeviceById(material.pumpId);
    if (!pump) {
      error = `No pump for ${material.code}!`;
      logger.warn(error, material);
      return { error };
    }

    let mft, waterTargets: { headWater: number; washWater: number } | any = {};
    if ((this.materialStore.isWater(materialId) || this.materialStore.isSecondWater(materialId)) && !data.materialId) {
      mft = this.getTargetForWater(constituent, data);
      waterTargets = this.getHeadWashWaterTarget(data, mft, material.code);
      mft = waterTargets.headWater + waterTargets.washWater;
    } else {
      mft = this.getTargetForMetered(material, materialDetails, constituent, data);
    }

    if (!mft) {
      error = `No target for ${material.code}!`;
      logger.warn(error, material);
      return { error };
    }

    if (pump.unitId && pump.unitId !== materialDetails.itemBatchUnitId) {
      const unit = this.getUnitById(pump.unitId);
      if (unit && unit !== materialDetails.itemBatchUnitSymbol) {
        mft = convertUnit(mft, materialDetails.itemBatchUnitSymbol, unit);
        waterTargets = {
          headWater: convertUnit(waterTargets.headWater, materialDetails.itemBatchUnitSymbol, unit),
          washWater: convertUnit(waterTargets.washWater, materialDetails.itemBatchUnitSymbol, unit),
        };
      }
    }

    return new MeteredDataToBatch({
      constituent,
      material,
      materialDetails,
      pump,
      mft: toNumber(mft, 2),
      headWater: toNumber(waterTargets.headWater, 2),
      washWater: toNumber(waterTargets.washWater, 2),
    });
  },

  getWeighedDataToBatch(this: IBatchStore, materialId: number, constituent: any, data: IBatchData, binId: number) {

    let error = '';
    binId = Number(binId);

    const material = constituent?.batchCode ? this.materialStore.getMaterialByBatchCode(constituent.batchCode) : this.materialStore.getMaterialByItemId(materialId);

    if (!material) {
      error = `No material: #${materialId} on the plant! `;
      logger.warn(error);
      return { error };
    }

    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    if (!materialDetails) {
      error = `No material details: ${material.code}! `;
      logger.warn(error, material);
      return { error };
    }

    if (materialDetails.verification && materialDetails.verificationMessage) {
      this.showInfo(`${material.code}: ${materialDetails.verificationMessage}`);
    }

    const materialUnit = materialDetails.itemBatchUnitSymbol;
    let scale = this.devicesStore.getDeviceById(material.scaleId);
    let primaryBin = null;
    let binForDecum = null;

    if (!scale?.decumulative) {
      if (binId) {
        primaryBin = this.devicesStore.getDeviceById(binId);
      } else {
        primaryBin = data.useBay && data.bay
          ? this.devicesStore.getBinByBay(material, data.bay) || this.devicesStore.getPrimaryBin(material)
          : this.devicesStore.getPrimaryBin(material);
      }

      if (!primaryBin) {
        error = `No bin for ${material.code}! `;
        logger.warn(error, material);
        return { error };
      }

      logger.log('bin for material:', material.code, primaryBin.description, 'bay:', data.bay, 'use bay:', data.useBay);

      scale = this.devicesStore.getDeviceById(primaryBin.scaleId);
      if (!scale) {
        error = `Bin #${primaryBin.id} has no scale: #${primaryBin.scaleId} for ${material.code}! `;
        logger.warn(error, material, primaryBin);
        return { error };
      }
    } else {
      // decum scale
      binForDecum = this.devicesStore.getPrimaryBin(material);
      if (!binForDecum) {
        error = `Decum Scale #${scale.id} has no bin set for ${material.code}! `;
        logger.warn(error, material);
        return { error };
      }
    }

    let mft;
    let proportionOneTarget = 0;
    let proportionTwoTarget = 0;
    let unconvertedProportionOneTarget = 0;
    let unconvertedProportionTwoTarget = 0;
    let overcapacityTarget = 0;
    let unconvertedOvercapacityTarget = 0;
    let convertedTarget;
    const batchUnit = materialDetails.itemBatchUnitSymbol;
    const deviceUnit = this.getUnitById(scale.unitId);
    const isDeferenceUnit = batchUnit ? batchUnit !== deviceUnit : false;

    if (!data.materialId && (this.materialStore.isWater(materialId) || this.materialStore.isSecondWater(materialId))) {
      mft = this.getTargetForWater(constituent, data);
      const waterTargets = this.getHeadWashWaterTarget(data, mft, material.code, scale, materialUnit);
      mft = waterTargets.headWater + waterTargets.washWater;
      convertedTarget = isDeferenceUnit ? toNumber(convertUnit(mft, batchUnit, deviceUnit), 5) : mft;

      if (convertedTarget > scale.capacity && scale.allowOvercapacityInOneDrop) {
        overcapacityTarget = convertedTarget - scale.capacity;
        unconvertedOvercapacityTarget = isDeferenceUnit ? toNumber(convertUnit(overcapacityTarget, deviceUnit, batchUnit), 5) : overcapacityTarget;
        logger.log('target over capacity', convertedTarget, scale.capacity, mft, waterTargets);
      }
    } else {
      mft = this.getTargetForWeighed(constituent, data);

      if (binId && constituent?.binPercent && Object.keys(constituent.binPercent).length) {
        const percent = constituent.binPercent[binId] || 0;
        mft = mft * percent/100;
      } else if (binId && data.materialId && data.binPercent && Object.keys(data.binPercent).length) {
        const percent = data.binPercent[binId] || 0;
        mft = mft * percent/100;
      }

      if (materialDetails.batchProportionTwo > 0) {
        const percent = materialDetails.batchProportionTwo;
        unconvertedProportionTwoTarget = mft * percent/100;
        unconvertedProportionOneTarget = mft - unconvertedProportionTwoTarget;
        // Round only once to ensure proportion targets sum to the rounded total target.
        const totalTargetRounded = toNumber(isDeferenceUnit ? toNumber(convertUnit(mft, batchUnit, deviceUnit), 5) : mft);
        proportionTwoTarget = isDeferenceUnit
          ? toNumber(convertUnit(unconvertedProportionTwoTarget, batchUnit, deviceUnit))
          : toNumber(unconvertedProportionTwoTarget);
        proportionTwoTarget = Math.min(proportionTwoTarget, totalTargetRounded);
        proportionOneTarget = totalTargetRounded - proportionTwoTarget;
        logger.log('proportion:', material.code, percent, mft, proportionOneTarget, proportionTwoTarget);
      }
    }

    convertedTarget = isDeferenceUnit ? toNumber(convertUnit(mft, batchUnit, deviceUnit), 5) : mft;
    logger.log('target', material.code, batchUnit, deviceUnit, mft, convertedTarget, overcapacityTarget, unconvertedOvercapacityTarget, data);

    return new WeighedDataToBatch({
      constituent,
      material,
      materialDetails,
      binPercent: constituent?.binPercent ? constituent.binPercent : data.binPercent || {},
      bin: primaryBin,
      binForDecum,
      scale,
      mft: toNumber(convertedTarget),
      unconvertedTarget: toNumber(mft),
      overcapacityTarget: toNumber(overcapacityTarget),
      unconvertedOvercapacityTarget: toNumber(unconvertedOvercapacityTarget),
      proportionOneTarget,
      proportionTwoTarget,
      unconvertedProportionOneTarget,
      unconvertedProportionTwoTarget,
      jogSecondary: false,
      jogCounter: 0,
    });
  },

  getFinalScale(this: IBatchStore, scale: IScale) {

    const parentScale = this.devicesStore.getDeviceById(scale.parentScaleId);
    if (parentScale) {
      return this.getFinalScale(parentScale);
    }
    return scale;
  },

  getDataForPumpsAndScales(this: IBatchStore, initialData: IBatchData) {

    if (!initialData) {
      return;
    }

    initialData = { ...initialData };

    let scales: { key: string, data: any } | any = {};
    let pumps: {} | any = {};
    let finalScales: {} | any = {};

    if (initialData.materialId) {
      const material = this.materialStore.getMaterialByItemId(initialData.materialId);

      if (!material) {
        logger.warn(`No material: #${initialData.materialId} on the plant! `);
        return { error: `No material: #${initialData.materialId} on the plant!` };
      }

      if (material.assignment === MaterialType.Weighed) {

        let dataToBatch;
        if (Object.keys(initialData.binPercent).length) {
          Object.keys(initialData.binPercent).forEach(binId => {

            dataToBatch = this.getWeighedDataToBatch(initialData.materialId, null, initialData, Number(binId));

            if (dataToBatch.error) {
              logger.warn(dataToBatch.error, initialData);
              return { error: dataToBatch.error };
            }

            if (initialData.binPercent[binId]) {
              if (!scales[dataToBatch.scale.id]) {
                scales[dataToBatch.scale.id] = [];
              }

              scales[dataToBatch.scale.id].push(dataToBatch);

              const fs = this.getFinalScale(dataToBatch.scale);
              finalScales[fs.id] = fs;
            }
          });

        } else {
          dataToBatch = this.getWeighedDataToBatch(initialData.materialId, null, initialData);

          if (dataToBatch.error) {
            logger.warn(dataToBatch.error, initialData);
            return { error: dataToBatch.error };
          }

          if (!scales[dataToBatch.scale.id]) {
            scales[dataToBatch.scale.id] = [];
          }

          scales[dataToBatch.scale.id].push(dataToBatch);

          const fs = this.getFinalScale(dataToBatch.scale);
          finalScales[fs.id] = fs;
        }

      } else if (material.assignment === MaterialType.Metered) {
        const dataToBatch = this.getMeteredDataToBatch(initialData.materialId, null, initialData);

        if (dataToBatch.error) {
          logger.warn(dataToBatch.error, initialData);
          return { error: dataToBatch.error };
        }
        pumps[material.pumpId] = dataToBatch;
      }

      initialData.scales = Object.keys(scales).length ? scales : null;
      initialData.pumps = Object.keys(pumps).length ? pumps : null;
      initialData.finalScales = Object.keys(finalScales).length ? finalScales : null;

      return initialData;
    }

    if (!initialData.mixDesign) {
      logger.warn('No mixDesign !!!', initialData);
      return { error: 'No mixDesign !' };
    }

    initialData.mixDesign.forEach((constituent) => {
      const material = constituent.batchCode
        ? this.materialStore.getMaterialByBatchCode(constituent.batchCode)
        : this.materialStore.getMaterialByItemId(constituent.constituentItemId);

      if (!material) {
        logger.warn(`No material: #${constituent.constituentItemCode} on the plant! `);
        return { error: `No material: #${constituent.constituentItemCode} on the plant! ` };
      }

      // Nothing to feed: its target goes on the ticket when the drop starts (saveTargetsForSkipMaterialsWithoutDevice).
      if (this.materialStore.isSkipMaterialWithoutDevice(constituent)) {
        return;
      }

      if (constituent.quantityFinal > 0) {
        if (material.assignment === MaterialType.Weighed) {

          let dataToBatch;
          if (constituent?.binPercent && Object.keys(constituent?.binPercent).length) {
            Object.keys(constituent.binPercent).forEach(binId => {

              dataToBatch = this.getWeighedDataToBatch(constituent.constituentItemId, constituent, initialData, Number(binId));

              if (dataToBatch.error) {
                logger.warn(dataToBatch.error, initialData);
                return { error: dataToBatch.error };
              }

              if (constituent.binPercent[binId]) {
                if (dataToBatch.mft) {
                  if (!scales[dataToBatch.scale.id]) {
                    scales[dataToBatch.scale.id] = [];
                  }

                  scales[dataToBatch.scale.id].push(dataToBatch);

                  const fs = this.getFinalScale(dataToBatch.scale);
                  finalScales[fs.id] = fs;
                }
              }
            });

          } else {
            dataToBatch = this.getWeighedDataToBatch(constituent.constituentItemId, constituent, initialData);

            if (dataToBatch.error) {
              logger.warn(dataToBatch.error, initialData);
              return { error: dataToBatch.error };
            }

            if (dataToBatch.mft) {
              if (!scales[dataToBatch.scale.id]) {
                scales[dataToBatch.scale.id] = [];
              }

              scales[dataToBatch.scale.id].push(dataToBatch);

              const fs = this.getFinalScale(dataToBatch.scale);
              finalScales[fs.id] = fs;
            }
          }

        } else if (material.assignment === MaterialType.Metered) {
          const dataToBatch = this.getMeteredDataToBatch(constituent.constituentItemId, constituent, initialData);

          if (dataToBatch.error) {
            logger.warn(dataToBatch.error, initialData);
            return { error: dataToBatch.error };
          }

          pumps[material.pumpId] = dataToBatch;

        }
      }
    });

    initialData.scales = Object.keys(scales).length ? scales : null;
    initialData.pumps = Object.keys(pumps).length ? pumps : null;
    initialData.finalScales = Object.keys(finalScales).length ? finalScales : null;

    return initialData;
  },

  sendBatchCycleFeedStartEvent(this: IBatchStore, scaleId: number) {

    const scaleType = this.dischargeStore.getTypeForScale(scaleId);

    let batchStatusType;
    if (scaleType === ScaleTypes.AGG) {
      batchStatusType = BatchCycleStatus.START_AGG_FEED;
    } else if (scaleType === ScaleTypes.CEM) {
      batchStatusType = BatchCycleStatus.START_CEM_FEED;
    } else if (scaleType === ScaleTypes.WATER) {
      batchStatusType = BatchCycleStatus.START_WATER_FEED;
    }

    if (batchStatusType) {
      logger.log('batch cycle:', batchStatusType);
      this.context.onBatchCycle(batchStatusType, this.inputs, this.outputs, this.channels);
    }
  },

  checkDataInScaleQueue(this: IBatchStore) {

    const data = Object.keys(this.scaleQueue);

    if (!data.length || this.isAbort) {
      return;
    }

    logger.log('checkDataInScaleQueue');

    data.forEach((scaleId: number | string) => {
      scaleId = toNumber(scaleId);
      if (this.scaleQueue[scaleId].length) {
        if (this.isScaleReadyForBatch(scaleId, this.scaleQueue[scaleId][0])) {
          const batchData = this.scaleQueue[scaleId][0];
          const r1 = this.running.length ? this.running[0] : null;
          const mixerStatus = r1?.mixerId ? this.getDeviceStatus(r1.mixerId) : {};
          if (
            !this.checkIfFirstMaterialForScaleIsTurboBatch(batchData, scaleId)
            && (
              r1 && (r1.uid !== batchData.uid || r1.currentDrop !== batchData.currentDrop)
              && !ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING.includes(mixerStatus.status)
            )
          ) {
            logger.log('wait in scale queue', scaleId);
            this.timers.clear(timerKey.checkDataInScaleQueue());
            this.timers.set(timerKey.checkDataInScaleQueue(), () => this.checkDataInScaleQueue(), 2000, 'Check data in scale queue');
            return;
          }

          const queueData = this.scaleQueue[scaleId].shift();
          logger.log(`data in scale queue ${scaleId}, shift`, queueData);
          this.isOverride = false;

          this.batchDataForScale(scaleId, queueData);

        } else {
          this.timers.clear(timerKey.checkDataInScaleQueue());
          this.timers.set(timerKey.checkDataInScaleQueue(), () => this.checkDataInScaleQueue(), 2000, 'Check data in scale queue');
        }
      }
    });

  },

  isOneOfScalesReady(this: IBatchStore, dataForScales: any) {

    let isReady = false;
    const r = this.running;
    const mixerStatus = r.length === 1 && r[0].mixerId ? this.getDeviceStatus(r[0].mixerId) : {};

    Object.keys(dataForScales).forEach(scaleId => {
      const scale = this.devicesStore.getDeviceById(Number(scaleId));
      const ds = this.getDeviceStatus(scale.id);

      if (ds.status === DeviceStatus.IDLE || ds.status === DeviceStatus.FINISH) {
        const d = dataForScales[scaleId].length ? dataForScales[scaleId][0] : null;

        if ((d?.materialDetails?.turboBatch
          && (!this.scaleQueue[scaleId]?.length))
          || (!r.length || (ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING.includes(mixerStatus.status)))) {
          isReady = true;
        }
      }
    });

    logger.log('one of the scales is ready for batch', isReady, 'mixer:', mixerStatus.status);

    return isReady;
  },

  arePumpsReadyForBatch(this: IBatchStore, data: IBatchData) {
    return data.pumps && !this.running.length;
  },

  holdScalesClosed(this: IBatchStore) {

    const { configuration } = this.settingsStore;
    let closePorts: number[] = [];
    let openPorts: number[] = [];

    if (configuration.holdScalesClosed) {
      this.devicesStore.scales.forEach((scale: IScale) => {
        const ds = this.getDeviceStatus(scale.id);

        if (ds.status === DeviceStatus.IDLE || ds.status === DeviceStatus.FINISH) {
          if (scale.closeGateOnePNo) {
            closePorts.push(scale.closeGateOnePNo);
          }
          if (scale.openGateOnePNo) {
            openPorts.push(scale.openGateOnePNo);
          }
          if (scale.closeGateTwoPNo) {
            closePorts.push(scale.closeGateTwoPNo);
          }
          if (scale.openGateTwoPNo) {
            openPorts.push(scale.openGateTwoPNo);
          }
          if (scale.closeGateThreePNo) {
            closePorts.push(scale.closeGateThreePNo);
          }
          if (scale.openGateThreePNo) {
            openPorts.push(scale.openGateThreePNo);
          }
        }
      });

      if (closePorts.length) {
        this.setOutputs(closePorts.map(v => { return { port: v, value: OUTPUT_TTL };}));
      }
      if (openPorts.length) {
        this.setOutputs(openPorts.map(v => { return { port: v, value: 0 };}));
      }
    }

    logger.log('hold scales closed', configuration.holdScalesClosed, openPorts, closePorts);
  },

  setLoadStartTime(this: IBatchStore, data: IBatchData, loadStartTime = moment().tz(this.timezone).format(), loadStartTimeDiff = 0) {

    this.queue.forEach(v => {
      if (v.uid === data.uid) {
        v.loadStartTime = loadStartTime;
        v.loadStartTimeDiff = loadStartTimeDiff;
      }
    });

    this.running.forEach(v => {
      if (v.uid === data.uid) {
        v.loadStartTime = loadStartTime;
        v.loadStartTimeDiff = loadStartTimeDiff;
      }
    });

    return loadStartTime;
  },

  canBatchMetered(this: IBatchStore, data: IBatchData, pumpData: IMeteredDataToBatch) {

    const MAX_RUNNING_BATCHES = 2;
    if (this.running.length && data.uid !== this.running[0].uid) {
      if (this.running.length <= MAX_RUNNING_BATCHES && this.running[0].mixerId) {
        const mixerStatus = this.getDeviceStatus(this.running[0].mixerId);

        if (!ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING.includes(mixerStatus.status)) {
          logger.log('pump not ready for discharge, data from different ticket, mixer not mixing yet');
          return false;
        }

      } else {
        logger.log('pump not ready for discharge, other ticket still in progress:',
          pumpData?.material?.code, pumpData?.pump.id, data.uid);
        return false;
      }
    }

    return true;
  },

  canStartBatchingNextTicketInQueue(this: IBatchStore, queuedUid: string) {

    const MAX_RUNNING_BATCHES = 2;
    const { configuration } = this.settingsStore;

    if (this.running.length && this.running[0].uid !== queuedUid && !configuration.enableTurboTicket) {
      logger.log('warning, data in the queue from different ticket');

      if (!this.running[0].mixerId) {
        logger.log('warning, other ticket still in progress');
        return false;
      }

      const mixerStatus = this.getDeviceStatus(this.running[0].mixerId);

      if (!ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING.includes(mixerStatus.status) && this.running.length === 1) {
        logger.log(
          'warning, data in the queue from different ticket, mixer not mixing yet',
          mixerStatus.status,
        );
        return false;
      }
    }

    if (this.running.length >= MAX_RUNNING_BATCHES && !configuration.enableTurboTicket) {
      logger.log(`warning, maximum running data: ${MAX_RUNNING_BATCHES}`);
      return false;
    }

    return true;
  },

  startBatch(this: IBatchStore) {

    logger.log('startBatch');
    if (!this.queue.length) {
      return;
    }

    if (this.canSendRemoteInstruction()) {
      this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.START_QUEUE));
      return;
    }

    let data = { ...this.queue[0] };
    data = this.getDataForPumpsAndScales(data);

    if (data?.error) {
      this.showError(data.error);
      return;
    }

    if (!data.scales && !data.pumps) {
      return;
    }

    const { configuration } = this.settingsStore;

    logger.log('turbo batch next ticket:', configuration.enableTurboTicket);

    // Ensure the previous ticket is at least already in the mixer or gone
    if (!this.canStartBatchingNextTicketInQueue(data.uid)) {
      return;
    }

    const now = Date.now();
    const MIN_WAIT_TIME_FOR_NEXT_LOAD = 5 * 1000;

    if (this.running.length && this.lastLoadStartTime && (now - this.lastLoadStartTime) < MIN_WAIT_TIME_FOR_NEXT_LOAD) {
      logger.log(`next load must wait min ${MIN_WAIT_TIME_FOR_NEXT_LOAD} ms`, now - this.lastLoadStartTime);
      return;
    }

    this.isAbort = false;
    logger.log(`START BATCH: ${data.loadStartTime}, ticket: ${data.tId}, drop: ${data.currentDrop}/${data.allDrops}`);

    if (!configuration.keepBeltOffForPreloading) {
      this.clearBeltsTimers(); // Prevent belts to turn off after off delay
    }

    if (!this.running.length && data.currentDrop === 1) {
      this.clearTimers();
      this.setInitialData(false);
    }

    if (configuration.printAsSoonAsMaterialsAreWeighed && data.currentDrop === data.allDrops) {
      this.checkBatchProgress(data, (data) => {
        this.printAsSoonAsMaterialsAreWeighed(data);
      });
    }

    if (!data.scales && data.pumps) {
      if (!this.arePumpsReadyForBatch(data)) {
        return;
      }

      data = Object.assign(this.queue.shift(), data);
      this.saveTargetsForSkipMaterialsWithoutDevice(data);
      this.running.push(data);
      this.lastLoadStartTime = Date.now();

      this.onBatchAction(data, BatchAction.NoWait);
      this.onBatchAction(data, BatchAction.StartBatching);

      if (data.isWashout) {
        this.onBatchAction(data, BatchAction.StartDischarging);
      } else {
        this.context.onBatchCycle(BatchCycleStatus.START, this.inputs, this.outputs, this.channels);

        if (data.currentDrop === 1) {
          this.context.onBatchCycle(BatchCycleStatus.START_LOAD, this.inputs, this.outputs, this.channels);
        }
      }

      return;
    }

    if (data.scales && !this.isOneOfScalesReady(data.scales)) {
      return;
    }

    data = Object.assign(this.queue.shift(), data);
    this.saveTargetsForSkipMaterialsWithoutDevice(data);

    if (data.currentDrop === 1) {
      data.loadStartTime = this.setLoadStartTime(data);
    }

    this.saveDropTimes(data, {
      dropNumber: data.currentDrop,
      loadStartTime: moment().tz(this.timezone).format(),
    });

    this.running.push(data);
    this.lastLoadStartTime = Date.now();

    if (!data.isWashout) {
      this.context.onBatchCycle(BatchCycleStatus.START, this.inputs, this.outputs, this.channels);

      if (data.currentDrop === 1) {
        const { connectionStore } = this.rootStore;

        if (
          data.mixDesign && connectionStore.isOnline && !data.batchStartedOffline
          && Number.isInteger(data.id)
          && (!this.isDemo || (this.isDemo && configuration.enableDemo))
        ) {
          this.ticketStore.startBatchTicket(data.id)
            .then((v: any) => {
              const diff = moment(data.loadStartTime).tz(this.timezone)
                .diff(moment(v.loadStartTime).tz(this.timezone)) || 0;
              this.setLoadStartTime(data, v.loadStartTime, diff);

              this.saveDropTimes(data, {
                dropNumber: data.currentDrop,
                loadStartTime: v.loadStartTime,
              });
            });
        }

        this.context.onBatchCycle(BatchCycleStatus.START_LOAD, this.inputs, this.outputs, this.channels);
        this.context.onBatchCycle(BatchCycleStatus.START_LOAD, this.inputs, this.outputs, this.channels);
      }
    }

    this.scheduleStore.deletePreSavedOrder(data.id);
    this.holdScalesClosed();

    this.timers.set(timerKey.startBatch(), () => {

      this.showTargetsOnPumps(data);

      Object.keys(data.scales).forEach((scaleId: string | number) => {
        scaleId = toNumber(scaleId);

        if (!this.scaleQueue[scaleId]) {
          this.scaleQueue[scaleId] = [];
        }

        if (!this.isScaleReadyForBatch(scaleId, data)) {
          if (!this.scaleQueue[scaleId].length || !this.scaleQueue[scaleId].find((v: IBatchData) => v.uid === data.uid && v.currentDrop === data.currentDrop)) {
            this.scaleQueue[scaleId].push(data);
            logger.log(`scale #${scaleId} is not ready! pushing to scale queue this.scaleQueue[${scaleId}]`, data);
          }

          this.timers.clear(timerKey.checkDataInScaleQueue());
          this.timers.set(timerKey.checkDataInScaleQueue(), () => this.checkDataInScaleQueue(), 2000, 'Check data in scale queue');

          return;
        }

        if (this.scaleQueue[scaleId].length) {
          this.scaleQueue[scaleId].push(data);
          const queueData = this.scaleQueue[scaleId].shift();
          logger.log(`scale queue ${scaleId} not empty, push & shift`, data, queueData);

          this.setActiveBinByBay(scaleId, queueData);
          this.showTargetOnBins(scaleId, queueData);
          this.batchDataForScale(scaleId, queueData);
        } else {
          this.setActiveBinByBay(scaleId, data);
          this.showTargetOnBins(scaleId, data);
          this.batchDataForScale(scaleId, data);
        }

      });
    }, 900);

  },

};
